import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { bankTransactionsApi } from "../../services/api";
import { BankTransaction } from "@akfdev-com/shared/src/models";

/**
 * Evidence attachments for a bank transaction's explanation (reconciliation
 * Req 13). Files are uploaded to S3 via a pre-signed URL (never a manual URL),
 * their object key recorded on the transaction, and listed with a pre-signed
 * download link. Attachments are retained even if the transaction is unmatched.
 */

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

function filenameFromKey(key: string): string {
  const parts = key.split("/");
  return parts[parts.length - 1] || key;
}

export interface TransactionAttachmentsProps {
  transaction: BankTransaction;
  buildingId: string;
  /** Called after a successful upload so the parent can refetch the tx. */
  onChange?: () => void;
}

const TransactionAttachments: React.FC<TransactionAttachmentsProps> = ({
  transaction,
  buildingId,
  onChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const attachments = transaction.attachments ?? [];

  const handleFile = async (file: File) => {
    if (file.size > MAX_FILE_BYTES) {
      toast.error("File is too large (max 10MB).");
      return;
    }
    setUploading(true);
    try {
      const { uploadUrl, key } = await bankTransactionsApi.getAttachmentUploadUrl(
        transaction.id,
        buildingId,
        file.name,
        file.type || "application/octet-stream",
      );
      // Upload directly to S3. The presigned PUT was signed with KMS SSE, so the
      // matching header must be sent for the signature to validate.
      const put = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
          "x-amz-server-side-encryption": "aws:kms",
        },
      });
      if (!put.ok) throw new Error(`S3 upload failed (${put.status})`);
      await bankTransactionsApi.addAttachment(transaction.id, buildingId, key);
      toast.success(`Attached ${file.name}.`);
      onChange?.();
    } catch (err) {
      console.error("Attachment upload failed:", err);
      toast.error("Could not upload the attachment. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (key: string) => {
    try {
      const { downloadUrl } = await bankTransactionsApi.getAttachmentDownloadUrl(
        transaction.id,
        buildingId,
        key,
      );
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Attachment download failed:", err);
      toast.error("Could not open the attachment.");
    }
  };

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-3 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
        Attachments
      </p>

      {attachments.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {attachments.map((key) => (
            <li key={key} className="flex items-center justify-between gap-3">
              <span className="min-w-0 truncate text-gray-800">
                {filenameFromKey(key)}
              </span>
              <button
                type="button"
                onClick={() => handleDownload(key)}
                className="shrink-0 text-primary-600 hover:underline"
              >
                Download
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-gray-400">No attachments yet.</p>
      )}

      <div className="mt-3">
        <input
          ref={fileInputRef}
          type="file"
          aria-label="Attach evidence file"
          disabled={uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
          className="block w-full text-xs text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-primary-700 hover:file:bg-primary-100 disabled:opacity-50"
        />
        {uploading && (
          <p className="mt-1 text-xs text-gray-500">Uploading…</p>
        )}
      </div>
    </div>
  );
};

export default TransactionAttachments;
